export const knowledgeBase = {
  title: 'Knowledge Base',
  subtitle: 'Curate the sources your content generation should treat as ground truth.',

  sourceType: {
    url: 'URL',
    pdf: 'PDF',
    manual: 'Manual',
    content: 'From post',
  },

  status: {
    pending: 'Pending',
    indexing: 'Indexing',
    ready: 'Ready',
    failed: 'Failed',
  },

  actions: {
    newSource: 'New source',
    reindex: 'Re-index',
    refresh: 'Refresh cache',
    delete: 'Delete',
    openMenu: 'Open actions menu',
  },

  list: {
    authoritative: 'Authoritative',
    expiringSoon: 'Expiring soon',
    untitled: 'Untitled source',
    columns: {
      title: 'Source',
      type: 'Type',
      status: 'Status',
      tags: 'Tags',
      updated: 'Updated',
      actions: 'Actions',
    },
    empty: {
      title: 'No sources yet',
      description: 'Add your first URL, PDF, manual note or published post to get started.',
    },
    filters: {
      searchPlaceholder: 'Search sources…',
      status: {
        all: 'All statuses',
        ready: 'Ready',
        indexing: 'Indexing',
        pending: 'Pending',
        failed: 'Failed',
      },
      expiring: {
        any: 'Any expiration',
        '7': 'Expires ≤ 7 days',
        '30': 'Expires ≤ 30 days',
        '90': 'Expires ≤ 90 days',
      },
    },
  },

  addDialog: {
    title: 'Add to knowledge base',
    description: 'Pick where the source comes from. All sources are indexed and reused across generations.',
  },

  picker: {
    back: 'Back',
    disclaimer:
      'Only upload content you own or have explicit permission to use. Sources are scoped to this organization only.',
    url: {
      title: 'From a URL',
      description: 'We extract readable content from a public page.',
      formTitle: 'Add URL source',
      formDescription: 'We pull the <title> and main text of the page automatically.',
    },
    pdf: {
      title: 'From a PDF',
      description: 'Upload a PDF and we will store it for you.',
      formTitle: 'Add PDF source',
      formDescription: 'Upload the file and we will extract the text automatically.',
    },
    manual: {
      title: 'Manual text',
      description: 'Paste T&Cs, FAQs or internal docs directly.',
      formTitle: 'Add manual source',
      formDescription: 'Use this mode for text that does not live at a public URL.',
    },
    content: {
      title: 'From a published post',
      description: 'Reference an existing post in this organization.',
      formTitle: 'Add source from post',
      formDescription: 'We use the latest completed version of the post as the body.',
    },
    crawl: {
      title: 'Crawl website',
      description:
        'Discover every URL of a site via sitemap or crawl and pick which ones to import as sources.',
    },
  },

  crawl: {
    tabs: {
      sitemap: 'Sitemap',
      crawl: 'Crawl',
    },
    input: {
      title: 'Crawl website',
      description: 'Discover the URLs of a site and pick which ones to import.',
    },
    sitemap: {
      urlLabel: 'Starting URL',
      urlHelp: 'Paste the site root. We automatically look up /sitemap.xml.',
      submit: 'Discover URLs',
      submitting: 'Discovering…',
    },
    crawl: {
      urlLabel: 'Starting URL',
      maxPagesLabel: 'Max pages',
      maxDepthLabel: 'Max depth',
      submit: 'Start crawl',
      submitting: 'Starting…',
      progress: 'Crawling… {{crawled}} crawled, {{discovered}} discovered.',
    },
    review: {
      title: 'Discovered URLs',
      description: 'Pick which URLs to import. All selected URLs share the metadata below.',
      summary: '{{discovered}} URLs discovered · {{selected}} selected',
      searchPlaceholder: 'Filter URLs…',
      selectAllVisible: 'Select all visible',
      deselectAll: 'Deselect all visible',
      noMatches: 'No URLs match the filter.',
      metadataTitle: 'Shared metadata',
      importCta: 'Import {{count}} URLs',
      importing: 'Importing…',
    },
  },

  form: {
    titleLabel: 'Title',
    titleUrlPlaceholder: 'Leave blank to derive from the page <title>',
    titlePdfPlaceholder: 'Leave blank to use the PDF filename',
    titleManualPlaceholder: 'E.g. Velo – Terms and conditions (2026-04)',
    titleAutoHint: 'Optional. Defaults to the URL if empty.',
    submitCreate: 'Create source',
    submitSave: 'Save changes',
    submitting: 'Saving…',

    errors: {
      invalidUrl: 'Enter a valid URL',
      pdfUrlRequired: 'Paste the PDF storage URL',
      titleRequired: 'Title is required',
      bodyRequired: 'Body is required',
      invalidContentId: 'Pick a valid post',
      fileRequired: 'Attach a PDF file',
      fileMustBePdf: 'File must be a PDF',
      fileTooLarge: 'PDF exceeds the 10 MB limit',
    },

    url: {
      urlLabel: 'Public URL',
    },
    pdf: {
      hint: 'Upload the PDF to your storage (S3/CDN) first, then paste the resulting URL. PDF file upload from the browser will land in v2.',
      urlLabel: 'PDF URL',
      uploadHint: 'Upload the PDF directly. We store it and extract the text for you.',
      fileLabel: 'PDF file',
      dropZoneTitle: 'Click to choose a PDF',
      dropZoneHint: 'PDF · up to 10 MB',
      replaceFile: 'Replace',
      removeFile: 'Remove file',
      uploading: 'Uploading…',
    },
    manual: {
      bodyLabel: 'Source body',
      bodyPlaceholder: 'Paste legal text, internal documentation, FAQ answers, or any ground-truth content…',
    },
    content: {
      hint: 'Reference an already-published post in this organization. The current version body is used as the source.',
      idLabel: 'Content ID (UUID)',
      pickerLabel: 'Source post',
      pickerPlaceholder: 'Pick a published post…',
      pickerSearchPlaceholder: 'Filter by title…',
      pickerLoading: 'Loading posts…',
      pickerEmpty: 'No matches. Try another term.',
    },

    metadata: {
      sectionTitle: 'Metadata',
      advancedToggle: 'Advanced settings (optional)',
      tagsLabel: 'Tags (comma-separated)',
      tagsPlaceholder: 'legal, tarifario, q2-2026',
      tagsHelp: 'Use tags to filter sources and to guide retrieval relevance.',
      staleLabel: 'Stale TTL (hours)',
      effectiveFromLabel: 'Effective from',
      expiresAtLabel: 'Expires at',
      authoritativeLabel: 'Authoritative source',
      authoritativeHelp:
        'Authoritative sources take precedence during content generation and must not go stale in production.',
    },
  },

  detail: {
    title: 'Source detail',
    description: 'Review the generated summary, refresh the cache, and update freshness controls.',
    notFound: 'Source not found.',
    authoritative: 'Authoritative',
    indexErrorLabel: 'Indexing error',
    lastIndexed: 'Last indexed',
    cacheInfo: 'Cache: {{wordCount}} words, fetched {{fetchedAt}}',
    summaryHeading: 'AI summary',
    summaryPending: 'The summary will be available once indexing completes.',
    bodyPreviewHeading: 'Body preview',
    metadataHeading: 'Metadata',
  },

  deleteDialog: {
    title: 'Delete source?',
    description: 'The source "{{title}}" will be soft-deleted and excluded from retrieval immediately.',
    confirm: 'Delete',
    cancel: 'Cancel',
    deleting: 'Deleting…',
  },

  toast: {
    createSuccess: 'Source created',
    createError: 'Failed to create source: {{message}}',
    updateSuccess: 'Source updated',
    updateError: 'Failed to update source: {{message}}',
    reindexQueued: 'Re-indexing triggered',
    reindexError: 'Failed to reindex: {{message}}',
    refreshSuccess: 'Cache refreshed',
    refreshError: 'Failed to refresh cache: {{message}}',
    deleteSuccess: 'Source deleted',
    deleteError: 'Failed to delete source: {{message}}',
    bulkCreateSuccess: 'Import done: {{created}} created, {{skipped}} skipped.',
    bulkCreateError: 'Bulk import failed: {{message}}',
    uploadPdfSuccess: 'PDF uploaded and source created',
  },
} as const
